/**
 * The route-cut intro draws the trip by hand, in screen space, on top of the film. These are the pure
 * pieces of that drawing -- no map, no React -- so they can be tested without either.
 *
 * Screen space on purpose: the map's own lines are geographic (a flight is a great circle, which at
 * country scale is a ruler-straight stroke), and the intro is not the map. It is a sketch of the trip
 * on a postcard, and a sketch bows its flights.
 */

type XY = number[];

const lerp = (a: XY, b: XY, t: number) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

/**
 * A flight, as a bow from `a` to `b`. It bends away from `away` -- the middle of the whole trip -- so
 * every arc of a round trip bulges outwards and none of them cross the country the wrong way.
 */
export const bowedArc = (a: XY, b: XY, away: XY, bow = 0.2, maxBow = Infinity, samples = 48): number[][] => {
	const chord = Math.hypot(b[0] - a[0], b[1] - a[1]);
	if (chord < 1) return [a, b];
	const mid = lerp(a, b, 0.5);
	// Unit normal to the chord, pointed away from the trip's centre.
	let nx = -(b[1] - a[1]) / chord;
	let ny = (b[0] - a[0]) / chord;
	if ((mid[0] - away[0]) * nx + (mid[1] - away[1]) * ny < 0) {
		nx = -nx;
		ny = -ny;
	}
	const h = Math.min(chord * bow, maxBow);
	// Quadratic Bezier whose peak is `h` off the chord: the control point sits at twice that.
	const c = [mid[0] + nx * h * 2, mid[1] + ny * h * 2];
	const out: number[][] = [];
	for (let i = 0; i <= samples; i++) {
		const t = i / samples;
		const u = 1 - t;
		out.push([u * u * a[0] + 2 * u * t * c[0] + t * t * b[0], u * u * a[1] + 2 * u * t * c[1] + t * t * b[1]]);
	}
	return out;
};

/**
 * A road, a ferry or a coastline, smoothed through its own points (Catmull-Rom), so a thinned
 * polyline stops reading as corners. The country outlines are 1:110m, where a coast is a handful of
 * straight segments; smoothed, the same points read as drawn.
 */
export const smoothed = (pts: number[][], perSegment = 8): number[][] => {
	if (pts.length < 3) return pts;
	const p = [pts[0], ...pts, pts[pts.length - 1]];
	const out: number[][] = [];
	for (let i = 1; i < p.length - 2; i++) {
		const [p0, p1, p2, p3] = [p[i - 1], p[i], p[i + 1], p[i + 2]];
		for (let s = 0; s < perSegment; s++) {
			const t = s / perSegment;
			const t2 = t * t;
			const t3 = t2 * t;
			out.push(
				[0, 1].map(
					(k) => 0.5 * (2 * p1[k] + (-p0[k] + p2[k]) * t + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3)
				)
			);
		}
	}
	out.push(pts[pts.length - 1]);
	return out;
};

/** Where along a polyline a given distance falls, and which way it is heading there (degrees, screen space). */
export const pointAlong = (xy: number[][], distance: number): { at: XY; heading: number; index: number } => {
	if (xy.length === 0) return { at: [0, 0], heading: 0, index: 0 };
	if (xy.length === 1) return { at: xy[0], heading: 0, index: 0 };
	let run = 0;
	for (let i = 1; i < xy.length; i++) {
		const seg = Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1]);
		if (run + seg >= distance || i === xy.length - 1) {
			const t = seg > 0 ? Math.max(0, Math.min(1, (distance - run) / seg)) : 0;
			const heading = (Math.atan2(xy[i][1] - xy[i - 1][1], xy[i][0] - xy[i - 1][0]) * 180) / Math.PI;
			return { at: lerp(xy[i - 1], xy[i], t), heading, index: i - 1 };
		}
		run += seg;
	}
	return { at: xy[xy.length - 1], heading: 0, index: xy.length - 1 };
};

/** `scale` is below 1 when a column has more cards than fit its band at full size. */
export type CardSlot = { x: number; y: number; side: "left" | "right"; scale: number };

/**
 * Where each stop's polaroid hangs: in a column beside the country, at roughly its stop's height.
 *
 * A card goes on the side its stop is on, unless that side is already carrying more than half of
 * them -- five stops down one coast would otherwise stack five cards in one column and none in the
 * other. Cards in a column do not cover each other: the name is at the bottom of a card, and a
 * pinboard where you cannot read the names is just photos. A column that would not fit the band at
 * full size shrinks instead, and is shifted back up if pushing the cards apart ran it past the bottom.
 */
export const layoutCards = (
	stops: XY[],
	opts: { centerX: number; width: number; cardW: number; cardH: number; top: number; bottom: number; margin: number; left?: number; right?: number }
): CardSlot[] => {
	const { centerX, width, cardW, cardH, top, bottom, margin } = opts;
	// The columns stand at the frame's edges, or at the edges of the part of it that is theirs.
	const left = opts.left ?? 0;
	const right = opts.right ?? width;
	const n = stops.length;
	const cap = Math.ceil(n / 2);
	const sides: ("left" | "right")[] = stops.map((s) => (s[0] < centerX ? "left" : "right"));
	(["left", "right"] as const).forEach((side) => {
		const other = side === "left" ? "right" : "left";
		// Move the stops nearest the middle across first: they are the least committed to a side.
		const mine = sides.map((s, i) => i).filter((i) => sides[i] === side);
		mine
			.sort((a, b) => Math.abs(stops[a][0] - centerX) - Math.abs(stops[b][0] - centerX))
			.slice(0, Math.max(0, mine.length - cap))
			.forEach((i) => (sides[i] = other));
	});

	// A little air between cards, because a tilted card swings its corners out by a few per cent.
	const SPACING = 1.04;
	const colScale = (side: "left" | "right") => {
		const n = sides.filter((s) => s === side).length;
		return n ? Math.min(1, (bottom - top) / (n * cardH * SPACING)) : 1;
	};
	const slots: CardSlot[] = stops.map((s, i) => {
		const k = colScale(sides[i]);
		const h = cardH * k;
		const w = cardW * k;
		return {
			side: sides[i],
			scale: k,
			x: sides[i] === "left" ? left + margin + w / 2 : right - margin - w / 2,
			y: Math.max(top + h / 2, Math.min(bottom - h / 2, s[1])),
		};
	});

	(["left", "right"] as const).forEach((side) => {
		const k = colScale(side);
		const gap = cardH * k * SPACING;
		const col = slots
			.map((s, i) => i)
			.filter((i) => slots[i].side === side)
			.sort((a, b) => slots[a].y - slots[b].y);
		for (let k = 1; k < col.length; k++) {
			const prev = slots[col[k - 1]];
			const cur = slots[col[k]];
			if (cur.y - prev.y < gap) cur.y = prev.y + gap;
		}
		const last = col.length ? slots[col[col.length - 1]] : null;
		const overflow = last ? last.y - (bottom - (cardH * k) / 2) : 0;
		if (overflow > 0) col.forEach((i) => (slots[i].y -= overflow));
		// Every other card in a column steps in a little, so a stack reads as pinned by hand.
		col.forEach((i, k) => {
			if (k % 2) slots[i].x += (side === "left" ? 1 : -1) * cardW * slots[i].scale * 0.16;
		});
	});
	return slots;
};

/** Evenly spread picks that always include the first and the last. */
export const spread = (n: number, max: number) => (n <= max ? [...Array(n).keys()] : [...Array(max).keys()].map((k) => Math.round((k * (n - 1)) / (max - 1))));

/** A small deterministic generator, so the same trip always gets the same hand. */
export const seededRandom = (seed: number) => {
	let s = Math.abs(Math.floor(seed)) % 2147483646 || 1;
	return () => {
		s = (s * 16807) % 2147483647;
		return (s - 1) / 2147483646;
	};
};

/**
 * A number in [0, 1) for every integer, scattered: for a value that must change every frame. Not
 * `seededRandom(frame)()`: a Lehmer generator's first draw moves by a hair between neighbouring seeds,
 * so a split-flap seeded by the frame showed the same "random" letter frame after frame.
 */
export const hash01 = (n: number) => {
	let h = n | 0;
	h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
	h = Math.imul(h ^ (h >>> 16), 0x45d9f3b);
	h ^= h >>> 16;
	return (h >>> 0) / 4294967296;
};

/**
 * `seededRandom` for things seeded by their index -- a page, a ticket, a stamp. The same trap as the
 * flaps: seeded 40, 41, 42, every passport page's first draw was 0.0003 and every stamp leaned the same
 * -11 degrees. Scattering the seed first makes neighbours differ from the first draw on.
 */
export const scatteredRandom = (seed: number) => seededRandom(1 + Math.floor(hash01(seed) * 2147483645));

/** A stable number from a string, for seeding. */
export const hashSeed = (text: string) => Array.from(text || "").reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) % 2147483647, 7);

/**
 * The circle someone draws on a map with a marker: never a circle. An ellipse, a little tilted,
 * wobbling as the hand goes round, and carried past its own start so the two ends overlap without
 * meeting -- the second half runs slightly wider than the first, because a hand drifts outwards.
 * Returned as points along the stroke, start to finish.
 */
export const scribbleLoop = (
	cx: number,
	cy: number,
	rx: number,
	ry: number,
	tiltDeg: number,
	seed = 7,
	samples = 96,
	/** How loose the hand is: 0 is nearly an ellipse, 1 the default wobble and overshoot, 2 hurried. */
	looseness = 1
): number[][] => {
	const rand = seededRandom(seed);
	const start = -Math.PI * (0.35 + 0.3 * rand());
	const overshoot = (0.45 + 0.35 * rand()) * (0.4 + 0.6 * looseness);
	const phaseA = rand() * Math.PI * 2;
	const phaseB = rand() * Math.PI * 2;
	const wobbleA = (0.03 + 0.03 * rand()) * looseness;
	const wobbleB = (0.015 + 0.02 * rand()) * looseness;
	const drift = (0.07 + 0.05 * rand()) * (0.5 + 0.5 * looseness);
	const tilt = (tiltDeg * Math.PI) / 180;
	const sweep = Math.PI * 2 + overshoot;
	const out: number[][] = [];
	for (let i = 0; i <= samples; i++) {
		const u = i / samples;
		const a = start + sweep * u;
		const r = 1 + wobbleA * Math.sin(2 * a + phaseA) + wobbleB * Math.sin(3 * a + phaseB) + drift * (u - 0.5);
		const x = Math.cos(a) * rx * r;
		const y = Math.sin(a) * ry * r;
		out.push([cx + x * Math.cos(tilt) - y * Math.sin(tilt), cy + x * Math.sin(tilt) + y * Math.cos(tilt)]);
	}
	return out;
};

/**
 * A marker stroke as a filled outline: thin where the pen lands and lifts, full width in between.
 * SVG strokes have one width along their whole length, which is exactly what makes a drawn circle
 * look drawn by a machine.
 */
export const markerStroke = (pts: number[][], width: number): string => {
	if (pts.length < 2) return "";
	const n = pts.length;
	const left: number[][] = [];
	const right: number[][] = [];
	pts.forEach((p, i) => {
		const a = pts[Math.max(0, i - 1)];
		const b = pts[Math.min(n - 1, i + 1)];
		const len = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1;
		const nx = -(b[1] - a[1]) / len;
		const ny = (b[0] - a[0]) / len;
		const t = i / (n - 1);
		const land = Math.min(1, t / 0.08);
		const lift = Math.min(1, (1 - t) / 0.12);
		const w = (width / 2) * (0.35 + 0.65 * Math.min(land, lift));
		left.push([p[0] + nx * w, p[1] + ny * w]);
		right.push([p[0] - nx * w, p[1] - ny * w]);
	});
	const ring = [...left, ...right.reverse()];
	return ring.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ") + " Z";
};
